import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil, Observable, firstValueFrom, of, BehaviorSubject } from 'rxjs';
import { map, filter, first, switchMap } from 'rxjs/operators';
import { Branch } from 'src/app/core/interfaces/branch.interface';
import { BranchService } from 'src/app/core/services/branch.service';
import { LocalizationService } from '../../services/localization.service';
import { UsersService } from 'src/app/modules/users/services/users.service';
import { JwtService } from 'src/app/core/services/jwt.service';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-branch-selection-modal',
  templateUrl: './branch-selection-modal.component.html',
  styleUrls: ['./branch-selection-modal.component.scss']
})
export class BranchSelectionModalComponent implements OnInit, OnDestroy {
  @Input() showDontShowAgain = true; // Default to true for login, false for navbar
  @Input() allowedBranchIds: number[] = []; // Allowed branch IDs to filter branches
  @Output() branchSelected = new EventEmitter<Branch>();
  
  branchForm: FormGroup;
  branches: Branch[] = [];
  filteredBranches: Branch[] = [];
  selectedBranch: Branch | null = null;
  dontShowAgain = false;
  isLoading = false;
  currentLanguage = 'en';
  
  private destroy$ = new Subject<void>();
  private readonly DONT_SHOW_AGAIN_KEY_PREFIX = 'branch_selection_dont_show_again_user_';
  private readonly SELECTED_BRANCH_KEY_PREFIX = 'selected_branch_id_user_';
  private readonly SELECTED_BRANCH_OBJECT_KEY_PREFIX = 'selected_branch_object_user_';
  private branchesLoaded$ = new BehaviorSubject<boolean>(false);
  
  private getDontShowAgainKey(): string {
    const token = this.authService.getAccessToken();
    if (!token) return 'branch_selection_dont_show_again';
    const userId = this.jwtService.getUserId(token);
    return userId ? `${this.DONT_SHOW_AGAIN_KEY_PREFIX}${userId}` : 'branch_selection_dont_show_again';
  }
  
  private getSelectedBranchKey(): string {
    const token = this.authService.getAccessToken();
    if (!token) return 'selected_branch_id';
    const userId = this.jwtService.getUserId(token);
    return userId ? `${this.SELECTED_BRANCH_KEY_PREFIX}${userId}` : 'selected_branch_id';
  }

  private getSelectedBranchObjectKey(): string {
    const token = this.authService.getAccessToken();
    if (!token) return 'selected_branch_object';
    const userId = this.jwtService.getUserId(token);
    return userId ? `${this.SELECTED_BRANCH_OBJECT_KEY_PREFIX}${userId}` : 'selected_branch_object';
  }

  constructor(
    public activeModal: NgbActiveModal,
    public branchService: BranchService,
    private localizationService: LocalizationService,
    private usersService: UsersService,
    private jwtService: JwtService,
    private authService: AuthService,
    private toastr: ToastrService,
    private router: Router
  ) {
    this.branchForm = new FormGroup({
      branchId: new FormControl(null, [Validators.required])
    });
  }

  ngOnInit(): void {
    this.loadBranches();
    this.loadSavedPreferences();
    
    this.localizationService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.currentLanguage = lang;
      });

    this.branchForm.get('branchId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(branchId => {
        if (branchId) {
          // First check in filtered branches, then all branches
          let branch = this.filteredBranches.find(b => b.id === branchId) || 
                       this.branches.find(b => b.id === branchId);
          
          if (branch) {
            this.selectedBranch = branch;
          } else {
            // If branch not found in loaded branches, fetch it from API
            this.branchService.getBranch(branchId)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (fetchedBranch) => {
                  this.selectedBranch = this.branchFromGetBranchResponse(fetchedBranch);
                },
                error: (err) => {
                  console.error('Error fetching branch:', err);
                  this.selectedBranch = null;
                }
              });
          }
        } else {
          this.selectedBranch = null;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadBranches(): void {
    this.isLoading = true;

    // Get user profile first to get userId
    this.usersService.getUserProfile()
      .pipe(
        switchMap((profileRes: any) => {
          const userId = profileRes?.data?.id;
          if (!userId) {
            this.toastr.error('User ID not found');
            this.isLoading = false;
            return of({ data: { branchPermissions: [] } });
          }
          return this.usersService.getUserBranchPermissionsList(userId);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res: any) => {
          // Handle different possible response structures
          let branchData: any[] = [];
          
          // Structure 1: data.branchPermissions array
          if (res?.data?.branchPermissions && Array.isArray(res.data.branchPermissions)) {
            branchData = res.data.branchPermissions.map((b: any) => ({
              id: b.branchId || b.branch?.id,
              nameEn: b.branchNameEn || b.branch?.nameEn,
              nameAr: b.branchNameAr || b.branch?.nameAr
            }));
          }
          // Structure 2: data.branches array with nested branch objects
          else if (res?.data?.branches && Array.isArray(res.data.branches)) {
            branchData = res.data.branches
              .map((item: any) => item.branch || item)
              .filter((branch: any) => branch?.id !== 0 && branch?.id !== undefined)
              .map((branch: any) => ({
                id: branch.id,
                nameEn: branch.nameEn,
                nameAr: branch.nameAr
              }));
          }

          // Map the response to Branch objects with fallback labels
          this.branches = branchData.map((branchInfo: any) => {
            const branch: Branch & { isActive: boolean } = {
              id: branchInfo.id,
              nameAr: branchInfo.nameAr || branchInfo.nameEn || '',
              nameEn: branchInfo.nameEn || branchInfo.nameAr || '',
              isActive: true
            };
            return branch;
          });

          // Filter branches by allowedBranchIds if provided
          if (this.allowedBranchIds && this.allowedBranchIds.length > 0) {
            this.filteredBranches = this.branches.filter(branch =>
              branch.id && this.allowedBranchIds.includes(branch.id)
            );
          } else {
            this.filteredBranches = this.branches;
          }

          // Set default selection to current branch BEFORE marking as loaded
          // This ensures the value is set before the select component initializes
          this.setDefaultBranch();
        },
        error: (err) => {
          console.error('Error loading branches:', err);
          this.toastr.error('Failed to load branches');
          this.isLoading = false;
        }
      });
  }

  private loadSavedPreferences(): void {
    const dontShowAgainKey = this.getDontShowAgainKey();
    const dontShowAgain = localStorage.getItem(dontShowAgainKey);
    this.dontShowAgain = dontShowAgain === 'true';
  }

  private setDefaultBranch(): void {
    // First, check localStorage for the user-specific saved branch (the user's last selection)
    const selectedBranchKey = this.getSelectedBranchKey();
    const savedBranchId = localStorage.getItem(selectedBranchKey);

    if (savedBranchId) {
      const branchId = parseInt(savedBranchId, 10);
      // Check if this branch exists in loaded branches AND is allowed
      const branch = this.filteredBranches.find(b => b.id === branchId) ||
                     this.branches.find(b => b.id === branchId);

      if (branch && (!this.allowedBranchIds.length || this.allowedBranchIds.includes(branchId))) {
        // Pre-select the branch the user last chose
        this.branchForm.patchValue({ branchId }, { emitEvent: false });
        this.selectedBranch = branch;

        // Mark branches as loaded AFTER setting the default value
        this.branchesLoaded$.next(true);
        this.isLoading = false;
        return;
      }
    }

    // If no localStorage branch, fall back to the API active branch
    this.branchService.getDefaultBranch(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data?.id) {
            const currentBranchId = response.data.id;

            // Check if current branch exists in filtered branches or all branches
            const currentBranch = this.filteredBranches.find(b => b.id === currentBranchId) ||
                                  this.branches.find(b => b.id === currentBranchId);

            if (currentBranch && (!this.allowedBranchIds.length || this.allowedBranchIds.includes(currentBranchId))) {
              this.branchForm.patchValue({ branchId: currentBranchId }, { emitEvent: false });
              this.selectedBranch = currentBranch;

              this.branchesLoaded$.next(true);
              this.isLoading = false;
              return;
            }
          }

          // If current branch not found or not in allowed list, try to find active branch
          this.setDefaultBranchFallback();
        },
        error: (err) => {
          console.error('Error fetching current branch:', err);
          this.setDefaultBranchFallback();
        }
      });
  }

  private setDefaultBranchFallback(): void {
    // Try to find the active branch
    const activeBranch = this.filteredBranches.find((b: any) => b.isActive === true) ||
                        this.branches.find((b: any) => b.isActive === true);
    
    if (activeBranch && activeBranch.id) {
      // Set the value before the select component initializes
      this.branchForm.patchValue({ branchId: activeBranch.id }, { emitEvent: false });
      this.selectedBranch = activeBranch;
      
      // Mark branches as loaded AFTER setting the default value
      this.branchesLoaded$.next(true);
      this.isLoading = false;
      return;
    }

    // If no active branch, try to load saved branch from localStorage
    const selectedBranchKey = this.getSelectedBranchKey();
    const savedBranchId = localStorage.getItem(selectedBranchKey);
    
    if (savedBranchId) {
      const branchId = parseInt(savedBranchId, 10);
      // Check if this branch exists in loaded branches AND is allowed
      const branch = this.filteredBranches.find(b => b.id === branchId) || 
                     this.branches.find(b => b.id === branchId);
      
      if (branch && (!this.allowedBranchIds.length || this.allowedBranchIds.includes(branchId))) {
        // This is the user's current selection from localStorage - use it (same as navbar)
        this.branchForm.patchValue({ branchId }, { emitEvent: false });
        this.selectedBranch = branch;
        
        // Mark branches as loaded AFTER setting the default value
        this.branchesLoaded$.next(true);
        this.isLoading = false;
        return;
      }
      // If branch not found in loaded branches or not allowed, continue to fallback
    }
    
    // If no branch found at all, still mark as loaded but don't set a default
    this.branchesLoaded$.next(true);
    this.isLoading = false;
  }

  /** Normalizes GET /Structure/Branch/:id (or of(null) when Branch.View is missing). */
  private branchFromGetBranchResponse(res: unknown): Branch | null {
    if (res == null || typeof res !== 'object') {
      return null;
    }
    const wrapped = res as { data?: unknown };
    const inner = wrapped.data !== undefined ? wrapped.data : res;
    if (inner != null && typeof inner === 'object' && 'id' in inner && (inner as Branch).id != null) {
      return inner as Branch;
    }
    return null;
  }

  /**
   * After updateSingleActiveBranch succeeds: reuse in-memory selection, else GET branch, else list row.
   * Compares ids numerically so string form values do not force a failing getBranch call.
   */
  private async resolveBranchModelAfterUpdate(branchIdRaw: number | string): Promise<Branch | null> {
    const branchId = Number(branchIdRaw);
    if (!Number.isFinite(branchId)) {
      return null;
    }

    if (this.selectedBranch != null && Number(this.selectedBranch.id) === branchId) {
      return this.selectedBranch;
    }

    const res = await firstValueFrom(this.branchService.getBranch(branchId));
    const fromApi = this.branchFromGetBranchResponse(res);
    if (fromApi) {
      return fromApi;
    }

    return (
      this.filteredBranches.find((b) => b.id === branchId) ??
      this.branches.find((b) => b.id === branchId) ??
      null
    );
  }

  async onSave(): Promise<void> {
    if (this.branchForm.invalid) {
      this.branchForm.markAllAsTouched();
      return;
    }

    const branchId = this.branchForm.get('branchId')?.value;
    if (!branchId) {
      this.branchForm.markAllAsTouched();
      return;
    }

    const token = this.authService.getAccessToken();
    if (!token) {
      this.toastr.error('Authentication required');
      return;
    }

    const userId = this.jwtService.getUserId(token);
    if (!userId) {
      this.toastr.error('User ID not found');
      return;
    }

    this.isLoading = true;
    try {
      await firstValueFrom(this.usersService.updateSingleActiveBranch(parseInt(userId, 10), branchId));
      
      // Save selected branch to localStorage (user-specific)
      const selectedBranchKey = this.getSelectedBranchKey();
      localStorage.setItem(selectedBranchKey, branchId.toString());

      // Save "don't show again" preference (user-specific)
      const dontShowAgainKey = this.getDontShowAgainKey();
      if (this.dontShowAgain) {
        localStorage.setItem(dontShowAgainKey, 'true');
      } else {
        localStorage.removeItem(dontShowAgainKey);
      }

      // Ensure we have the full branch object before emitting/closing.
      // getBranch() emits null when Branch.View is denied — do not read .data on null.
      this.selectedBranch = await this.resolveBranchModelAfterUpdate(branchId);
      if (!this.selectedBranch) {
        const idNum = Number(branchId);
        if (Number.isFinite(idNum)) {
          this.selectedBranch = { id: idNum, nameAr: '', nameEn: '' };
        }
      }

      // Emit and close with the full branch object
      if(this.selectedBranch) {
        // Save full object to localStorage
        const selectedBranchObjectKey = this.getSelectedBranchObjectKey();
        localStorage.setItem(selectedBranchObjectKey, JSON.stringify(this.selectedBranch));

        this.branchSelected.emit(this.selectedBranch);
        this.activeModal.close(this.selectedBranch);
        
        // Redirect to dashboard after branch selection
        this.router.navigate(['/dashboard']);
      }
    } catch (error) {
      console.error('Error updating active branch:', error);
      this.toastr.error('Failed to update active branch');
    } finally {
      this.isLoading = false;
    }
  }

  onClose(): void {
    this.activeModal.dismiss();
  }

  onDontShowAgainChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.dontShowAgain = target?.checked ?? false;
  }

  get branchIdControl(): FormControl {
    return this.branchForm.get('branchId') as FormControl;
  }

  getBranchLabel(branch: Branch): string {
    if (this.currentLanguage === 'ar') {
      return branch.nameAr || branch.nameEn || '';
    }
    return branch.nameEn || branch.nameAr || '';
  }

  // Custom fetch function that filters branches by allowedBranchIds
  // Since we're using branch-permissions/list, we filter from the already loaded branches
  filteredBranchFetch = (pageNumber: number, pageSize: number, term: string, filters?: any[]): Observable<any> => {
    const getData = () => {
      // Use the branches loaded from permissions
      const branchesToSearch = this.allowedBranchIds && this.allowedBranchIds.length > 0
        ? this.filteredBranches
        : this.branches;

      // Filter by search term
      let filteredData = branchesToSearch;
      if (term && term.trim()) {
        const searchTerm = term.toLowerCase();
        filteredData = branchesToSearch.filter((branch: Branch) => {
          const nameAr = (branch.nameAr || '').toLowerCase();
          const nameEn = (branch.nameEn || '').toLowerCase();
          return nameAr.includes(searchTerm) || nameEn.includes(searchTerm);
        });
      }

      // Apply pagination
      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);

      return {
        data: paginatedData,
        totalCount: filteredData.length,
        pageNumber: pageNumber,
        pageSize: pageSize
      };
    };

    // If branches are already loaded, return immediately
    if (this.branchesLoaded$.value) {
      return of(getData());
    }

    // Otherwise, wait for branches to be loaded
    return this.branchesLoaded$.pipe(
      filter(loaded => loaded === true),
      first(),
      takeUntil(this.destroy$),
      map(() => getData())
    );
  }

  // Fetch selected branch from already loaded branches instead of calling API
  fetchSelectedBranchFromLoaded = (branchId: any): Observable<any> => {
    const findBranch = () => {
      // First check in filtered branches, then all branches
      const branch = this.filteredBranches.find(b => b.id === branchId) || 
                     this.branches.find(b => b.id === branchId);
      
      if (branch) {
        // Return in the same format as branchService.getBranch would return
        return of({ data: branch });
      }
      
      // If branch not found, return null
      return of(null);
    };

    // If branches are already loaded, return immediately
    if (this.branchesLoaded$.value) {
      return findBranch();
    }

    // Otherwise, wait for branches to be loaded
    return this.branchesLoaded$.pipe(
      filter(loaded => loaded === true),
      first(),
      takeUntil(this.destroy$),
      switchMap(() => findBranch())
    );
  }
}

